import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";
import "@/index.css";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import StructuredData from "@/components/StructuredData";
import Analytics from "@/components/Analytics";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AuthProvider } from "@/context/AuthContext";

import Home from "@/pages/Home";
import AreasDeAtuacao from "@/pages/AreasDeAtuacao";
import Recrutamento from "@/pages/Recrutamento";
import Artigos from "@/pages/Artigos";
import ArtigoDetail from "@/pages/ArtigoDetail";
import OfficePage from "@/pages/OfficePage";
import Press from "@/pages/Press";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ArticleForm from "@/pages/admin/ArticleForm";
import AdminPress from "@/pages/admin/AdminPress";
import PressForm from "@/pages/admin/PressForm";

const ScrollToTop = () => {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (!hash) window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
};

// Public shell renders header/footer + public routes
const PublicSite = ({ children }) => (
    <>
        <Header />
        <main>{children}</main>
        <Footer />
        <WhatsAppButton />
    </>
);

function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <div className="App">
                    <BrowserRouter>
                        <ScrollToTop />
                        <Analytics />
                        <StructuredData />
                        <Routes>
                            {/* Admin routes (no public header/footer) */}
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route
                                path="/admin"
                                element={
                                    <ProtectedRoute>
                                        <AdminLayout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route index element={<AdminDashboard />} />
                                <Route
                                    path="artigos/novo"
                                    element={<ArticleForm />}
                                />
                                <Route
                                    path="artigos/:slug/editar"
                                    element={<ArticleForm />}
                                />
                                <Route path="press" element={<AdminPress />} />
                                <Route path="press/novo" element={<PressForm />} />
                                <Route path="press/:pressId/editar" element={<PressForm />} />
                            </Route>

                            {/* Public routes wrapped with header/footer */}
                            <Route
                                path="/"
                                element={
                                    <PublicSite>
                                        <Home />
                                    </PublicSite>
                                }
                            />
                            <Route
                                path="/areas-de-atuacao"
                                element={
                                    <PublicSite>
                                        <AreasDeAtuacao />
                                    </PublicSite>
                                }
                            />
                            <Route
                                path="/recrutamento"
                                element={
                                    <PublicSite>
                                        <Recrutamento />
                                    </PublicSite>
                                }
                            />
                            <Route
                                path="/artigos"
                                element={
                                    <PublicSite>
                                        <Artigos />
                                    </PublicSite>
                                }
                            />
                            <Route
                                path="/artigos/:slug"
                                element={
                                    <PublicSite>
                                        <ArtigoDetail />
                                    </PublicSite>
                                }
                            />
                            <Route
                                path="/press"
                                element={
                                    <PublicSite>
                                        <Press />
                                    </PublicSite>
                                }
                            />
                            <Route
                                path="/advogado-lisboa"
                                element={
                                    <PublicSite>
                                        <OfficePage slug="lisboa" />
                                    </PublicSite>
                                }
                            />
                            <Route
                                path="/advogado-ponta-delgada"
                                element={
                                    <PublicSite>
                                        <OfficePage slug="ponta-delgada" />
                                    </PublicSite>
                                }
                            />
                            {/* Legacy WordPress URL → 301 redirect to /artigos/:slug */}
                            <Route
                                path="/:slug"
                                element={<LegacyArticleRedirect />}
                            />
                        </Routes>
                        <Toaster richColors position="bottom-right" />
                    </BrowserRouter>
                </div>
            </AuthProvider>
        </HelmetProvider>
    );
}

// Redirect legacy /{slug} → /artigos/{slug} (permanent 301 equivalent in SPA)
function LegacyArticleRedirect() {
    const { slug } = useParams();
    return <Navigate to={`/artigos/${slug}`} replace />;
}

export default App;
